import { addMinutes, differenceInMilliseconds, isAfter } from "date-fns";

import type { ExtendedClassSessionSummaryType } from "api/api-data-types";
import {
  AttendanceStatus,
  type ClassSessionUserSummaryType,
  type OrgType,
} from "components/server-types";
import { formatLocalePattern } from "i18n/helpers";
import { t } from "i18n/i18n";

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

/** Minutes missed after which a student is absent when no org absent threshold is set. */
export const DEFAULT_ABSENT_THRESHOLD_MINUTES = 10;

export const ATTENDANCE_THRESHOLD_PROPERTIES = [
  "session_absent_threshold",
  "session_late_threshold",
] as const satisfies readonly (keyof OrgType)[];

export type AttendanceThresholdsType = Pick<
  OrgType,
  (typeof ATTENDANCE_THRESHOLD_PROPERTIES)[number]
>;

/** Minimum duration (minutes) for a session to appear in the session log. */
export const MIN_DURATION_CLASS_MINUTES = 5;

/** Minimum duration (minutes) for a session to qualify for engagement metrics. */
export const MIN_QUALIFYING_DURATION_MINUTES = 30;

/** Minimum attendance percentage (0–1) for a session to qualify for engagement metrics. */
export const MIN_QUALIFYING_ATTENDANCE_PERCENTAGE = 0.25;

/** Min qualifying sessions before attendance affects risk level in engagement table. */
export const MIN_QUALIFYING_SESSIONS_FOR_ATTENDANCE_RISK = 4;

/** Attendance threshold (0–1) below which risk increases by one level. */
export const ATTENDANCE_THRESHOLD_FOR_RISK_INCREASE = 0.7;

/** Attendance threshold (0–1) for medium risk when no effort/performance data. */
export const ATTENDANCE_THRESHOLD_FOR_MEDIUM_RISK = 0.85;

// -----------------------------------------------------------------------------
// Types & functions
// -----------------------------------------------------------------------------

export type AttendanceStatsType = {
  averageAttendance: number;
  highestAttendance: { percentage: number; date: string };
};

/** Compute attendance stats from ExtendedClassSessionSummaryType. */
export function computeAttendanceStats(
  extendedSummaries: Pick<
    ExtendedClassSessionSummaryType,
    "attendance_percentage" | "started_at"
  >[],
): AttendanceStatsType {
  const result = {
    averageAttendance: 0,
    highestAttendance: { percentage: 0, date: "" },
  };
  if (extendedSummaries.length === 0) return result;

  let totalAttendance = 0;
  let attendanceSessionCount = 0;

  for (const session of extendedSummaries) {
    const attendancePercentage = session.attendance_percentage;
    totalAttendance += attendancePercentage;
    attendanceSessionCount += 1;
    if (attendancePercentage > result.highestAttendance.percentage && session.started_at) {
      result.highestAttendance = {
        percentage: Math.round(attendancePercentage),
        date: formatLocalePattern(session.started_at, "common.formats.month_day_year"),
      };
    }
  }

  result.averageAttendance =
    attendanceSessionCount > 0 ? Math.round(totalAttendance / attendanceSessionCount) : 0;
  return result;
}

type StudentTalkTimeStatsType = {
  averageStudentTalkTime: number;
  highestStudentTalkTime: { percentage: number; date: string };
};

/** Compute student talk time stats from ExtendedClassSessionSummaryType. */
export function computeStudentTalkTimeStats(
  extendedSummaries: Pick<
    ExtendedClassSessionSummaryType,
    "student_talk_time_seconds" | "instructor_talk_time_seconds" | "started_at"
  >[],
): StudentTalkTimeStatsType {
  const result = {
    averageStudentTalkTime: 0,
    highestStudentTalkTime: { percentage: 0, date: "" },
  };
  if (extendedSummaries.length === 0) return result;

  let totalTalkTimePct = 0;
  let talkTimeSessionCount = 0;

  for (const session of extendedSummaries) {
    const studentSeconds = session.student_talk_time_seconds ?? 0;
    const instructorSeconds = session.instructor_talk_time_seconds ?? 0;
    const talkTimePercentage = getStudentTalkTimePercentage(studentSeconds, instructorSeconds);

    totalTalkTimePct += talkTimePercentage;
    talkTimeSessionCount += 1;

    if (talkTimePercentage > result.highestStudentTalkTime.percentage) {
      result.highestStudentTalkTime = {
        percentage: Math.round(talkTimePercentage),
        date: formatLocalePattern(session.started_at, "common.formats.month_day_year"),
      };
    }
  }

  result.averageStudentTalkTime =
    talkTimeSessionCount > 0 ? Math.round(totalTalkTimePct / talkTimeSessionCount) : 0;
  return result;
}

export type SessionWithDurationType = {
  effective_ended_at: string;
  started_at: string;
};

/** Keep in sync with ClassSessionUserSummary.compute_attendance_status. */
export function getAttendanceStatus(
  classSession: SessionWithDurationType,
  userSummary: Pick<ClassSessionUserSummaryType, "presence_minutes" | "first_seen"> | null,
  org: AttendanceThresholdsType | null,
): AttendanceStatus {
  if (userSummary == null) {
    return AttendanceStatus.Absent;
  }

  const presenceMinutes = userSummary.presence_minutes ?? 0;
  const absentThreshold = org?.session_absent_threshold ?? DEFAULT_ABSENT_THRESHOLD_MINUTES;
  const lateThreshold = org?.session_late_threshold ?? null;

  const duration = getClassSessionDurationInMinutes(classSession);
  const deltaMissed = duration - presenceMinutes;

  if (deltaMissed > absentThreshold) {
    return AttendanceStatus.Absent;
  }

  // Check if the student is late
  if (lateThreshold != null && userSummary.first_seen) {
    const firstSeen = new Date(userSummary.first_seen);
    const startedAt = new Date(classSession.started_at);

    if (isAfter(firstSeen, addMinutes(startedAt, lateThreshold))) {
      return AttendanceStatus.Late;
    }
  }

  return presenceMinutes > 0 ? AttendanceStatus.Present : AttendanceStatus.Absent;
}

export function isStudentPresent(
  classSession: SessionWithDurationType,
  userSummary: Pick<ClassSessionUserSummaryType, "presence_minutes" | "first_seen"> | null,
  org: AttendanceThresholdsType | null,
): boolean {
  return getAttendanceStatus(classSession, userSummary, org) !== AttendanceStatus.Absent;
}

export function getAttendanceStatusLabel(status: AttendanceStatus): string {
  return t(`session_log_view.attendance_status.${status}`);
}

export function getClassSessionDurationInMs(session: SessionWithDurationType): number {
  return differenceInMilliseconds(session.effective_ended_at, session.started_at);
}

/**
 * Minutes between start and effective end, rounded half-to-even.
 * Matches Python round() in ClassSessionUserSummary.compute_attendance_status.
 */
export function getClassSessionDurationInMinutes(session: SessionWithDurationType): number {
  const totalMs = getClassSessionDurationInMs(session);
  const magnitudeMs = Math.abs(totalMs);
  const wholeMinutes = Math.trunc(magnitudeMs / 60000);
  const remainderMs = magnitudeMs % 60000;

  const roundUp = remainderMs > 30000 || (remainderMs === 30000 && wholeMinutes % 2 !== 0);

  const minutes = wholeMinutes + (roundUp ? 1 : 0);

  if (minutes === 0 || totalMs >= 0) return minutes;

  return -minutes;
}

/**
 * Student share of total classroom talk time (student + instructor).
 * Instructor share is 100 - student when total > 0 so the two always sum to 100.
 */
export function getStudentTalkTimePercentage(
  studentTalkTimeSeconds: number,
  instructorTalkTimeSeconds: number,
): number {
  const total = studentTalkTimeSeconds + instructorTalkTimeSeconds;
  if (total <= 0) return 0;
  return Math.floor((studentTalkTimeSeconds / total) * 100);
}

export function getInstructorTalkTimePercentage(
  studentTalkTimeSeconds: number,
  instructorTalkTimeSeconds: number,
): number {
  const total = studentTalkTimeSeconds + instructorTalkTimeSeconds;
  if (total <= 0) return 0;
  return 100 - getStudentTalkTimePercentage(studentTalkTimeSeconds, instructorTalkTimeSeconds);
}
